import { ChangeLoginToken, cloneLogin } from '@mp/api/gateway/UserControler.ts'
import { ElMessage } from 'element-plus'
import { isWuJie } from '.'

/**
 * toOldB 使用说明
 *
 * 用途:
 *   用于从新版系统跳转到旧版B系统，同时安全地同步 accessToken，实现无缝登录。
 *
 * 用法:
 *   1. 在需要跳转到旧B系统的地方引入：
 *      import { toOldB } from '@mp/hooks'
 *
 *   2. 直接调用 toOldB 函数进行跳转：
 *      // 传递目标 url（相对旧B hash 地址）
 *      await toOldB({ url: '/crossRemit/overseaRemit' })
 *      // 若无参数，默认跳转至旧B首页
 *      await toOldB()
 *
 *   3. 可以配合 try-catch 处理错误：
 *      try {
 *        await toOldB({ url: '/some/path' })
 *      } catch (error) {
 *        console.error('跳转失败', error)
 *      }
 *
 * 技术细节:
 *   - 内部先请求 cloneLogin 接口获取 accessToken；
 *   - 用新 accessToken 调用 ChangeLoginToken 完成令牌更换；
 *   - 页面跳转到旧B指定路由，并带上最新 Token（旧B 会自动识别并登录）；
 *   - 错误时会通过 ElMessage 弹窗提示并抛出异常。
 *
 * 典型场景:
 *    - 升级中老功能跳转保留入口
 *    - 新旧系统数据无缝衔接
 */

/**
 * 跳转到旧版B系统
 * @param params 跳转参数
 * @param params.url 目标 url（相对旧B hash 地址），默认为 '/'
 * @returns Promise<boolean> 跳转成功返回 true，失败会抛出异常
 */
export async function toOldB(params?: {
  url?: string
  target?: '_blank' | '_self'
}): Promise<boolean> {
  try {
    // 获取 accessToken
    const res = await cloneLogin()
    const { data } = res as { data: { accessToken: string } }
    if (!data?.accessToken) {
      ElMessage.error('获取 accessToken 失败')
      throw new Error('获取 accessToken 失败')
    }

    // 更换 token
    const { resultCode } = await ChangeLoginToken({
      accessToken: data.accessToken,
      deviceId: window.deviceId as string,
    })
    if (resultCode !== 'SUCCESS') {
      ElMessage.error('更换token失败')
      throw new Error('更换token失败')
    }

    // 跳转到旧B系统
    const url = params?.url?.startsWith('http') ? params.url : `${import.meta.env.VITE_B_URL}/user.html#${params?.url || '/'}`
    const targetWindow = isWuJie ? window.parent : window

    if (params?.target === '_blank')
      window.open(url, '_blank')

    else
      targetWindow.location.href = url

    return true
  }
  catch (error) {
    // 如果错误还没有被处理，显示通用错误提示
    const isHandledError = error instanceof Error
      && (error.message.includes('获取') || error.message.includes('更换'))
    if (!isHandledError)
      ElMessage.error('跳转失败')
    throw error
  }
}

/**
 * @deprecated 请使用 toOldB 函数替代
 * 保留此 hook 以保持向后兼容，但建议直接使用 toOldB 函数
 */
export function useToOldB() {
  return {
    toOldB,
    isLoading: false, // 不再提供 loading 状态，因为函数是异步的，调用者可以通过 Promise 状态判断
  }
}
